import axios, { AxiosError } from 'axios'
import { useAuthStore } from '../context/auth'

/** Cliente HTTP con token de acceso y refresco automático ante 401. */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
})

api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState()
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
  return config
})

let refreshing: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setTokens, logout } = useAuthStore.getState()
  if (!refreshToken) return null
  try {
    const { data } = await axios.post(`${api.defaults.baseURL}/auth/refresh`, { refreshToken })
    setTokens(data.accessToken, data.refreshToken)
    return data.accessToken as string
  } catch {
    logout()
    return null
  }
}

api.interceptors.response.use(undefined, async (error: AxiosError) => {
  const original = error.config
  if (error.response?.status === 401 && original && !original.headers['X-Retried']) {
    refreshing ??= refreshAccessToken().finally(() => {
      refreshing = null
    })
    const token = await refreshing
    if (token) {
      original.headers.Authorization = `Bearer ${token}`
      original.headers['X-Retried'] = '1'
      return api.request(original)
    }
  }
  return Promise.reject(error)
})

/** Mensaje de error legible desde la respuesta uniforme de la API. */
export function apiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: { message?: string; details?: Array<{ message: string }> } } | undefined
    if (data?.error?.details?.length) return `${data.error.message}: ${data.error.details.map((d) => d.message).join(', ')}`
    if (data?.error?.message) return data.error.message
    if (error.code === 'ERR_NETWORK') return 'No hay conexión con el servidor'
  }
  return 'Error inesperado, intenta de nuevo'
}
